import {
  COMMERCIAL_DOMAIN_LIST,
  COMMERCIAL_DOMAIN_SCHEMAS,
  commercialFieldVisible,
} from "./commercialProjectSchema";
import { repeaterToArray } from "./commercialProjectAcf";
import {
  fileDisplayLabelFromStoredValue,
  fileUrlFromStoredValue,
} from "./CommercialFieldFileDisplay";

function isFileFieldType(type) {
  return type === "file" || type === "multifile";
}

function fileUrlFromValue(v) {
  return fileUrlFromStoredValue(v);
}

function mergeFileValue(serverVal, draftVal) {
  if (Array.isArray(serverVal)) {
    return serverVal;
  }
  const serverUrl = fileUrlFromValue(serverVal);
  if (serverUrl) return serverVal;
  if (serverVal === null || serverVal === "" || serverVal === false) {
    return serverVal;
  }
  if (serverVal === undefined) {
    return draftVal;
  }
  return serverVal;
}

const PERMIT_DATE_FIELD_KEYS = [
  "application_submitted_date",
  "permit_received_date",
  "permit_expiration_date",
];

/** Copy autofill date fields from server when the draft row has no value yet. */
function mergePermitDateFieldsFromServer(draftRow, serverRow) {
  const d = { ...draftRow };
  const s = serverRow || {};
  for (const key of PERMIT_DATE_FIELD_KEYS) {
    const sv = s[key];
    const dv = d[key];
    if (sv != null && String(sv).trim() && (dv == null || !String(dv).trim())) {
      d[key] = sv;
    }
  }
  return d;
}

function mergeRepeaterFileFields(draftRows, serverRows, repeaterFields) {
  const dArr = repeaterToArray(draftRows);
  const sArr = repeaterToArray(serverRows);
  // Keep null/false/undefined — coercing empty repeaters to [] causes spurious
  // changelog diffs (e.g. Fiber Files) when saving after a permit upload.
  if (dArr.length === 0 && sArr.length === 0) {
    return draftRows !== undefined ? draftRows : serverRows;
  }
  const len = Math.max(dArr.length, sArr.length);
  const out = [];
  for (let i = 0; i < len; i += 1) {
    let d = mergePermitDateFieldsFromServer(dArr[i] || {}, sArr[i] || {});
    const s = sArr[i] || {};
    for (const sub of repeaterFields) {
      if (isFileFieldType(sub.type)) {
        d[sub.key] = mergeFileValue(s[sub.key], d[sub.key]);
      }
    }
    out.push(d);
  }
  return out;
}

function mergePermitsRepeaterFromServer(draftRows, serverRows, repeaterFields) {
  const dArr = repeaterToArray(draftRows);
  const sArr = repeaterToArray(serverRows);
  if (dArr.length === 0 && sArr.length === 0) {
    return draftRows !== undefined ? draftRows : serverRows;
  }
  const len = Math.max(dArr.length, sArr.length);
  const out = [];
  for (let i = 0; i < len; i += 1) {
    let d = mergePermitDateFieldsFromServer(dArr[i] || {}, sArr[i] || {});
    const s = sArr[i] || {};
    for (const sub of repeaterFields) {
      if (isFileFieldType(sub.type)) {
        d[sub.key] = mergeFileValue(s[sub.key], d[sub.key]);
      }
    }
    // Keep draft materials. File-field merge used to copy server materials over the
    // draft, which dropped rows added in the editor before save.
    const draftMats = repeaterToArray(d.materials);
    const serverMats = repeaterToArray(s.materials);
    const draftHasMaterials = draftMats.some((row) => {
      const item = row?.item != null ? String(row.item).trim() : "";
      const slug = row?.slug != null ? String(row.slug).trim() : "";
      const quantity = row?.quantity != null ? String(row.quantity).trim() : "";
      return Boolean(item || slug || quantity);
    });
    if (!draftHasMaterials && serverMats.length) {
      d.materials = serverMats.map((row) => ({
        slug: row?.slug != null ? String(row.slug) : "",
        item: row?.item != null ? String(row.item) : "",
        quantity: row?.quantity != null ? String(row.quantity) : "",
      }));
    }
    if (s.materials_review_pending === true) {
      d.materials_review_pending = true;
    } else if (s.materials_review_pending === false) {
      d.materials_review_pending = false;
    }
    const ridersField = repeaterFields.find((f) => f.key === "riders");
    if (ridersField?.fields) {
      d.riders = mergeRepeaterFileFields(d.riders, s.riders, ridersField.fields);
    }
    out.push(d);
  }
  return out;
}

/** Apply permit-plan extraction payload onto draft engineering permits. */
export function applyPermitPlanMaterialsToDraft(draft, engineeringKey, permitIndex, materials) {
  if (!draft || !engineeringKey || !Array.isArray(materials) || !materials.length) return draft;
  const eng = { ...(draft[engineeringKey] || {}) };
  const permits = repeaterToArray(eng.permits);
  while (permits.length <= permitIndex) permits.push({});
  permits[permitIndex] = {
    ...(permits[permitIndex] || {}),
    materials: materials.map((row) => ({
      slug: row?.slug != null ? String(row.slug) : "",
      item: row?.item != null ? String(row.item) : "",
      quantity: row?.quantity != null ? String(row.quantity) : "",
    })),
    materials_review_pending: true,
  };
  return { ...draft, [engineeringKey]: { ...eng, permits } };
}

/** After a file upload, copy file/multifile values from server into draft without wiping text edits. */
export function mergeCommercialFileFieldsFromServer(draft, server) {
  if (!draft || !server) return draft;
  const out = { ...draft };
  for (const domainKey of COMMERCIAL_DOMAIN_LIST) {
    const schema = COMMERCIAL_DOMAIN_SCHEMAS[domainKey];
    if (!schema?.fields) continue;
    const dSlice = { ...(out[domainKey] || {}) };
    const sSlice = server[domainKey] || {};
    for (const field of schema.fields) {
      // Cross-domain aliases (e.g. Engineering asbuilt → construction_details) merge via the home domain.
      if (field.valueDomain && field.valueDomain !== domainKey) continue;
      if (field.type === "file") {
        dSlice[field.key] = mergeFileValue(sSlice[field.key], dSlice[field.key]);
      } else if (field.type === "multifile") {
        dSlice[field.key] = mergeFileValue(sSlice[field.key], dSlice[field.key]);
      } else if (field.type === "repeater" && field.fields) {
        if (field.key === "permits") {
          dSlice[field.key] = mergePermitsRepeaterFromServer(
            dSlice[field.key],
            sSlice[field.key],
            field.fields
          );
        } else {
          dSlice[field.key] = mergeRepeaterFileFields(
            dSlice[field.key],
            sSlice[field.key],
            field.fields
          );
        }
      }
    }
    out[domainKey] = dSlice;
  }
  return out;
}

export function collectCommercialFileEntries(domains, domainKey, ctx) {
  const schema = COMMERCIAL_DOMAIN_SCHEMAS[domainKey];
  const payload = domains?.[domainKey];
  if (!schema?.fields || !payload) return [];

  const entries = [];

  function walkFields(fields, data, pathPrefix, rowLabel) {
    if (!fields || !data) return;
    for (const field of fields) {
      if (field.showWhen && !commercialFieldVisible(field, ctx)) continue;
      const valueDomain = field.valueDomain && field.valueDomain !== pathPrefix[0] ? field.valueDomain : null;
      const sourceData = valueDomain ? domains?.[valueDomain] || {} : data;
      const fieldPathPrefix = valueDomain ? [valueDomain] : pathPrefix;
      const val = sourceData[field.key];
      if (field.type === "file") {
        const url = fileUrlFromValue(val);
        if (url) {
          entries.push({
            label: rowLabel ? `${rowLabel} — ${field.label}` : field.label,
            url,
            value: val,
            fieldKey: field.key,
            accept: field.accept,
            displayName: fileDisplayLabelFromStoredValue(val, {
              fieldKey: field.key,
              accept: field.accept,
            }),
            pathPrefix: [...fieldPathPrefix, field.key],
          });
        }
      } else if (field.type === "multifile") {
        const arr = Array.isArray(val) ? val : val ? [val] : [];
        arr.forEach((row, idx) => {
          const url = fileUrlFromValue(row);
          if (url) {
            entries.push({
              label: `${field.label} ${idx + 1}`,
              url,
              value: row,
              fieldKey: field.key,
              accept: field.accept,
              displayName: fileDisplayLabelFromStoredValue(row, {
                fieldKey: field.key,
                accept: field.accept,
              }),
              pathPrefix: [...fieldPathPrefix, field.key, idx, "site_walk_file"],
            });
          }
        });
      } else if (field.type === "repeater") {
        repeaterToArray(val).forEach((row, idx) => {
          walkFields(
            field.fields,
            row,
            [...fieldPathPrefix, field.key, idx],
            `${field.rowLabel || "Row"} ${idx + 1}`
          );
        });
      }
    }
  }

  walkFields(schema.fields, payload, [domainKey], "");
  return entries;
}
